// iter180 — Frontend mirror of /app/backend/core.py GROUP_PARENTS.
// SINGLE SOURCE OF TRUTH for parent-feature cascade on the frontend.
//
// When a parent feature is OFF, every child key listed under it is treated
// as OFF — regardless of its own stored flag. This drives navbar group
// visibility, ProtectedRoute gating, landing-page resolution, and the
// RestrictedHome fallback.
//
// Keep this file aligned with the backend GROUP_PARENTS dict. The backend
// is authoritative for API enforcement (middleware) — the frontend mirrors
// the same shape so the UI matches what the server will allow.

export const GROUP_PARENTS = {
  overview_home: ['dashboard'],
  financials_home: [
    'financial_dashboard', 'pnl_live', 'pnl_finalize_compare',
    'pnl_statements', 'review_sheet', 'petty_cash', 'cost_insights',
    'reports',
  ],
  operations_home: [
    'checklist', 'kitchen_closing', 'bar_closing', 'maintenance',
    'inventory_module', 'recipe_module', 'production_planner',
    'pricing_module', 'approvals',
  ],
  marketing_home: ['marketing', 'campaigns'],
  customers_home: [
    'crm', 'customer_feedback', 'custom_feedback_forms',
    'reservations_module', 'loyalty',
  ],
  communication_home: ['communication'],
  // iter180 — Settings is NOT in this cascade by design — it must always
  // remain accessible for profile / password / preferences.
  organization_home: ['user_management', 'outlet_management', 'audit_logs'],
  // iter183k — Strategy is the premium-tier umbrella. Expansion Intelligence
  // is the V1 child; future Strategy modules (Project Feasibility, Investment
  // Planning, Market Intelligence, Franchise Intelligence) extend this list.
  strategy_home: ['expansion_intelligence'],
  // POS Sprint 1 — Commerce parent gates the pos flag (and any future
  // commerce modules). MIRROR of GROUP_PARENTS['commerce_home'] in core.py.
  commerce_home: ['pos'],
};

// Reverse map: child_key → parent_key (for fast lookups).
export const CHILD_TO_PARENT = Object.entries(GROUP_PARENTS).reduce(
  (acc, [parent, children]) => {
    children.forEach((c) => { acc[c] = parent; });
    return acc;
  },
  {},
);

/**
 * Authoritative feature-enabled check WITH parent cascade.
 *
 * Returns true iff:
 *   - The feature itself is enabled (or unset → defaults to enabled)
 *   - AND its parent feature (if it has one) is also enabled
 *
 * Pass `null` / `undefined` for `feature` to mean "always allowed".
 */
export function isFeatureEnabled(flags, feature) {
  if (!feature) return true;
  if (!flags) return true;
  if (flags[feature] === false) return false;
  const parent = CHILD_TO_PARENT[feature];
  if (parent && flags[parent] === false) return false;
  return true;
}

/** Returns true if at least one child of the given parent is enabled
 *  (after cascade). Used to hide a navbar group entirely when its parent
 *  is OFF or all its children are disabled. */
export function isAnyChildEnabled(flags, parentKey) {
  if (!flags) return true;
  if (flags[parentKey] === false) return false;
  const children = GROUP_PARENTS[parentKey] || [];
  if (children.length === 0) return true;
  return children.some((c) => isFeatureEnabled(flags, c));
}
